import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, type ReactNode, type RefObject, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../components/UIToolkit/Animations/JumpingDots";
import { LongTaskStatusCard } from "../../../components/UIToolkit/molecules/LongTaskStatusCard";

import {
	SandboxedHtmlRenderer,
	type SandboxedHtmlRendererHandle,
	type SandboxRenderResult,
} from "../../../components/UIToolkit/Previews/SandboxedHtmlRenderer";

import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { AiPrintoutDataState } from "../../../hooks/useAiPrintoutData";
import { ALPHA, EVENT_LIST, LONG_TASK_CARD, MOTION } from "../../../theme/tokens";
import { CustomPrintoutLoadError } from "./CustomPrintoutLoadError";
import { CustomPrintoutPrintBar } from "./CustomPrintoutPrintBar";
import { CustomPrintoutStatusBanner } from "./CustomPrintoutStatusBanner";
import { PREVIEW_LOADER_DOT_PX } from "./consts";
import type { DraftPhase } from "./customPrintoutDraftState";
import { peopleOf } from "./customPrintoutPeople";
import type { CustomPrintoutCoverage } from "./customPrintoutTypes";

type Props = {
	dataState: AiPrintoutDataState;
	/** The template to draw: the draft, the saved printout, or the reference one; `null` while it loads. */
	source: string | null;
	/** The chosen printout couldn't be loaded. */
	sourceFailed: boolean;
	onRetrySource: () => void;
	report: SandboxRenderResult;
	onReport: (result: SandboxRenderResult) => void;
	phase: DraftPhase;
	/** What's on screen is an unsaved design (for the print analytics). */
	isDraft: boolean;
	canDesign: boolean;
	canFix: boolean;
	onFix: () => void;
	printoutId: string | null;
	/** What the printed page is titled — and so the file name browsers suggest when saving it as PDF. */
	printTitle: string;
	/** What the template shows. A summary isn't checked for everyone working the day, only for drawing. */
	coverage: CustomPrintoutCoverage;
	/** The popup's printout-usage logger, so a custom print counts like the others. */
	logPrintout: (action: "download" | "print", eventParams: Record<string, unknown>) => void;
	/** An earlier version is on screen: its number (for the print analytics), and the frame takes the brand ring. */
	viewedVersionNumber: number | null;
	/** Over the preview while an earlier version is on screen: which one, and the way back or to restore it. */
	versionBar: ReactNode;
};

/** What Annie is doing, for the card over the preview. Drawing a finished design is too quick for a card: dim only. */
const WORKING_TITLE_KEY: Partial<Record<DraftPhase, string>> = {
	generating: "printout.custom.ai.generating.title",
	repairing: "printout.custom.ai.repairing.title",
};

/**
 * The right pane of the Custom mode: the chosen template drawn for the viewed day in the sandbox, why printing is
 * off when it is, and Print — on only while every person on the day is visibly on the page. While Annie works,
 * the last drawing stays on screen, dimmed, under a card that says what she's doing and for how long.
 */
export const CustomPrintoutPreview: FC<Props> = (props) => {
	const { dataState, source, report, onReport, phase, coverage } = props;
	const { t } = useTranslation();
	const { data } = dataState;
	const rendererRef = useRef<SandboxedHtmlRendererHandle>(null);
	const [hasDrawn, setHasDrawn] = useState<boolean>(false);
	// A summary the user confirmed lists no one by design, so nobody is required on it.
	const required = useMemo(
		() => (data === null || coverage === "summary" ? [] : data.shifts.map((shift) => ({ id: shift.id, name: shift.name }))),
		[data, coverage],
	);
	const peopleCount = useMemo(() => (data === null ? 0 : peopleOf(data.shifts).length), [data]);
	const handlePrint = usePrint(props, rendererRef);

	const handleReport = useCallback(
		(result: SandboxRenderResult) => {
			if (result.status !== "rendering") setHasDrawn(true);
			onReport(result);
		},
		[onReport],
	);

	if (dataState.isError || props.sourceFailed) return <CustomPrintoutLoadError dayFailed={dataState.isError} onRetrySource={props.onRetrySource} />;
	const workingTitleKey = WORKING_TITLE_KEY[phase];
	const isDimmed = workingTitleKey !== undefined || phase === "rendering" || (hasDrawn && report.status === "rendering");
	const isEarlierVersion = props.viewedVersionNumber !== null;
	return (
		<Box data-testid="custom-printout-preview-pane" sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", gap: 2 }}>
			{props.versionBar}
			{data !== null && (
				<CustomPrintoutStatusBanner result={report} shifts={data.shifts} canDesign={props.canDesign} canFix={props.canFix} onFix={props.onFix} />
			)}
			<Box sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex" }}>
				{data !== null && source !== null && (
					<SandboxedHtmlRenderer
						ref={rendererRef}
						dataTestId="custom-printout-preview"
						title={t("printout.custom.ai.preview.title")}
						source={source}
						data={data}
						required={required}
						dir={data.dir}
						onReport={handleReport}
						sx={(theme) => ({
							flex: 1,
							opacity: isDimmed ? LONG_TASK_CARD.DIMMED_SURFACE_OPACITY : 1,
							// An earlier version is framed in the brand, so the page can't be mistaken for the current one. The frame
							// changes at once and only the dimming fades: border and shadow transitions repaint every frame.
							...(isEarlierVersion && {
								borderColor: alpha(theme.palette.primary.main, ALPHA.STATUS_BORDER),
								boxShadow: `0 0 0 ${EVENT_LIST.EMPHASIS_RING_PX}px ${alpha(theme.palette.primary.main, ALPHA.HALO)}`,
							}),
							transition: `opacity ${MOTION.DURATION.OVERLAY_FADE}ms ${MOTION.EASE_OUT}`,
							"@media (prefers-reduced-motion: reduce)": { transition: "none" },
						})}
					/>
				)}
				{(data === null || source === null || !hasDrawn) && (
					<Box
						role="status"
						aria-label={t("printout.custom.ai.loading")}
						data-testid="custom-printout-loading"
						sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}
					>
						<JumpingDots size={PREVIEW_LOADER_DOT_PX} color="primary.main" />
					</Box>
				)}
				{workingTitleKey !== undefined && (
					// Centred by a flex row rather than left/translate, so it stays centred in RTL too.
					<Box
						sx={{ position: "absolute", insetInline: 0, top: (theme) => theme.spacing(2), display: "flex", justifyContent: "center", pointerEvents: "none" }}
					>
						{/* A fresh card (and clock) per job: the design, then the automatic repair. */}
						<LongTaskStatusCard key={phase} dataTestId="custom-printout-working" title={t(workingTitleKey)} showElapsed />
					</Box>
				)}
			</Box>
			<CustomPrintoutPrintBar report={report} hasSource={source !== null} peopleCount={peopleCount} isSummary={coverage === "summary"} onPrint={handlePrint} />
		</Box>
	);
};

/** Logs and tracks the print the way every printout does, then hands the page to the browser's print dialog. */
const usePrint = (props: Props, rendererRef: RefObject<SandboxedHtmlRendererHandle | null>) => {
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { report, dataState, logPrintout, printoutId, printTitle, isDraft, viewedVersionNumber } = props;
	return useCallback(() => {
		if (report.status !== "rendered" || dataState.data === null) return;
		logPrintout("print", { downloadType: "custom", printoutId, isDraft });
		track("custom_printout_printed", {
			department_id: chosenDepartment.department_id,
			printout_id: printoutId,
			is_draft: isDraft,
			// `null` = the current version (or a draft); a number = an earlier version printed as shown.
			earlier_version_number: viewedVersionNumber,
			shift_count: dataState.data.shifts.length,
			render_ms: report.renderMs,
		});
		rendererRef.current?.print(printTitle);
	}, [report, dataState.data, logPrintout, printoutId, printTitle, isDraft, viewedVersionNumber, track, chosenDepartment.department_id, rendererRef]);
};
